<template>
  <div class="public-home-container">

    <div class="hero-banner">
      <div class="particles-container">
        <vue-particles
          id="tsparticles"
          :particlesInit="particlesInit"
          :options="particlesOptions"
        />
      </div>
      <div class="hero-content-overlay" data-aos="zoom-out" data-aos-duration="1000">
        <h1 class="hero-banner-title">{{ $t('hero.title') }}</h1>
        <p class="hero-banner-description">
          {{ $t('hero.subtitle') }}<br>
          <span class="highlight-text">{{ $t('hero.desc') }}</span>
        </p>
        <div class="hero-tags">
          <span>{{ $t('hero.tags.invest') }}</span> ·
          <span>{{ $t('hero.tags.talent') }}</span> ·
          <span>{{ $t('hero.tags.tech') }}</span> ·
          <span>{{ $t('hero.tags.policy') }}</span> ·
          <span>{{ $t('hero.tags.market') }}</span> ·
          <span>{{ $t('hero.tags.promo') }}</span>
        </div>
        <el-space :size="20" class="mt-4">
          <el-button type="primary" size="large" @click="goToLogin" class="tech-btn-primary">{{ $t('nav.login') }}</el-button>
          <el-button size="large" plain @click="goToNews" class="tech-btn-outline">{{ $t('nav.view_news') }}</el-button>
        </el-space>
      </div>
    </div>

    <div class="in-page-nav sticky-top">
      <div class="nav-container flex-container">
        <el-menu
          mode="horizontal"
          :default-active="activeSection"
          class="no-border-menu centered-menu"
          @select="scrollTo"
        >
          <el-menu-item index="about-us">{{ $t('nav.about') }}</el-menu-item>
          <el-menu-item index="latest-news">{{ $t('nav.news') }}</el-menu-item>
          <el-menu-item index="innovation-services">{{ $t('nav.services') }}</el-menu-item>
          <el-menu-item index="industry-insights">{{ $t('nav.insights') }}</el-menu-item>
          <el-menu-item index="partners">{{ $t('nav.partners') }}</el-menu-item>
          <el-menu-item index="contact-us">{{ $t('nav.contact') }}</el-menu-item>
        </el-menu>

        <div class="lang-switch-wrapper">
          <el-button link class="lang-btn" @click="toggleLang">
            {{ locale === 'zh' ? 'EN' : '中文' }}
          </el-button>
        </div>
      </div>
    </div>

    <div class="main-content">

      <section id="latest-news" class="content-section">
        <div class="section-header" data-aos="fade-up">
          <h2 class="section-title">{{ $t('section.news') }}</h2>
          <div class="section-subtitle">{{ $t('section.news_sub') }}</div>
        </div>
        <div v-if="newsList.length > 0">
          <div class="news-grid">
            <div v-for="(news, index) in newsList.slice(0, 3)" :key="news.id" class="news-wrapper" data-aos="fade-up" :data-aos-delay="index * 100">
              <div class="news-card" v-tilt @click="goToNewsDetail(news.id)">
                <div class="news-image-wrapper">
                  <img v-if="news.image" :src="news.image" class="news-image" alt="News">
                  <div v-else class="news-image no-image-placeholder"></div>
                  <div class="news-date-tag">{{ new Date(news.publish_date).toLocaleDateString() }}</div>
                </div>
                <div class="news-content">
                  <h3 class="news-title">{{ news.title }}</h3>
                  <p class="news-excerpt">{{ news.content.substring(0, 60) }}...</p>
                  <div class="read-more-link">阅读全文 <el-icon class="ml-1"><ArrowRight /></el-icon></div>
                </div>
              </div>
            </div>
          </div>
          <div class="news-btn-wrapper" data-aos="fade-up">
            <el-button @click="goToNews" round class="view-more-btn">查看所有新闻</el-button>
          </div>
        </div>
        <div v-else class="empty-news-state">
          <el-empty description="暂无最新动态" :image-size="100"></el-empty>
        </div>
      </section>

      <section id="innovation-services" class="content-section bg-light">
        <div class="section-header" data-aos="fade-up">
          <h2 class="section-title">{{ $t('section.services') }}</h2>
          <div class="section-subtitle">{{ $t('section.services_sub') }}</div>
        </div>
        <div class="bento-container" id="bento-grid-trigger">
          <div v-for="(item, index) in committees" :key="index" class="bento-item-wrapper" :class="`area-${index}`" data-aos="zoom-in" :data-aos-delay="index * 50">
            <div class="bento-card" v-tilt>
              <div class="card-bg-image">
                <img :src="item.image" :alt="item.title" />
                <div class="card-overlay"></div>
              </div>
              <div class="card-inner">
                <div class="card-content">
                  <div v-if="index === 0" class="highlight-tag">核心引擎</div>
                  <h3 class="card-title">{{ item.title }}</h3>
                  <p class="card-desc">{{ item.desc }}</p>
                </div>
                <div class="card-arrow"><el-icon><ArrowRight /></el-icon></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="industry-insights" class="content-section">
        <h2 class="section-title">{{ $t('section.insights') }}</h2>
        <div class="insights-container">
          <div class="policy-section glass-card" data-aos="fade-right">
            <div class="section-header">
              <h3 class="sub-title"><el-icon><DocumentCopy /></el-icon> 全国政策风向</h3>
              <el-link type="primary" :underline="false">更多 <el-icon><ArrowRight /></el-icon></el-link>
            </div>
            <ul class="policy-list">
              <li v-for="(policy, index) in policies" :key="index" class="policy-item" @click="openPolicyLink(policy.link)">
                <span class="policy-location">[{{ policy.location }}]</span>
                <span class="policy-title" :title="policy.title">{{ policy.title }}</span>
                <span class="policy-date">{{ policy.date }}</span>
              </li>
            </ul>
          </div>
          <div class="report-section" data-aos="fade-left">
            <h3 class="sub-title mb-3"><el-icon><DataAnalysis /></el-icon> 重点领域研究报告</h3>
            <div class="fields-grid">
              <div v-for="(field, index) in industryFields" :key="index" class="field-card" @click="goToReportList(field)">
                <div class="field-icon" :class="`field-color-${index}`">
                  <el-icon><component :is="field.icon" /></el-icon>
                </div>
                <div class="field-info">
                  <h4>{{ field.name }}</h4>
                  <p>{{ field.count }} 份报告</p>
                </div>
                <el-icon class="field-arrow"><ArrowRight /></el-icon>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="partners" class="content-section">
        <div class="section-header" data-aos="fade-up">
          <h2 class="section-title">{{ $t('section.partners') }}</h2>
          <div class="section-subtitle">{{ $t('section.partners_sub') }}</div>
        </div>
        <div v-if="researchGroups.length > 0" class="partner-category-block" data-aos="fade-up">
           <h3 class="category-title">科研机构</h3>
           <el-carousel :interval="5000" arrow="hover" indicator-position="none" height="200px">
             <el-carousel-item v-for="(group, index) in researchGroups" :key="index">
               <div class="partner-row">
                 <a v-for="partner in group" :key="partner.id" :href="partner.website" target="_blank" class="partner-item">
                   <div class="partner-logo-wrapper"><img :src="partner.logo" :alt="partner.name" class="partner-logo"></div>
                   <p class="partner-name">{{ partner.name }}</p>
                 </a>
               </div>
             </el-carousel-item>
           </el-carousel>
        </div>
        <div v-if="universityGroups.length > 0" class="partner-category-block" data-aos="fade-up">
           <h3 class="category-title">知名高校</h3>
           <el-carousel :interval="5000" arrow="hover" indicator-position="none" height="200px">
             <el-carousel-item v-for="(group, index) in universityGroups" :key="index">
               <div class="partner-row">
                 <a v-for="partner in group" :key="partner.id" :href="partner.website" target="_blank" class="partner-item">
                   <div class="partner-logo-wrapper"><img :src="partner.logo" :alt="partner.name" class="partner-logo"></div>
                   <p class="partner-name">{{ partner.name }}</p>
                 </a>
               </div>
             </el-carousel-item>
           </el-carousel>
        </div>
        <div v-if="enterpriseGroups.length > 0" class="partner-category-block" data-aos="fade-up">
           <h3 class="category-title">标杆企业</h3>
           <el-carousel :interval="5000" arrow="hover" indicator-position="none" height="200px">
             <el-carousel-item v-for="(group, index) in enterpriseGroups" :key="index">
               <div class="partner-row">
                 <a v-for="partner in group" :key="partner.id" :href="partner.website" target="_blank" class="partner-item">
                   <div class="partner-logo-wrapper"><img :src="partner.logo" :alt="partner.name" class="partner-logo"></div>
                   <p class="partner-name">{{ partner.name }}</p>
                 </a>
               </div>
             </el-carousel-item>
           </el-carousel>
        </div>
      </section>

      <section id="about-us" class="content-section">
        <div class="dark-card-wrapper" data-aos="fade-up" data-aos-duration="1000">
          <div class="about-container">
            <div class="about-left">
              <h2 class="section-header-left text-white">{{ $t('section.about') }} <span class="dot">.</span></h2>
              <p class="about-lead">
                {{ $t('about.lead_1') }}<br>
                <span style="color: #a0cfff; font-weight: 500;">{{ $t('about.lead_2') }}</span>
              </p>
              <div class="decoration-line"></div>
              <el-button type="primary" size="large" round class="about-btn">{{ $t('about.btn') }}</el-button>
            </div>

            <div class="about-right">
              <div v-for="(item, index) in serviceTargets" :key="index" class="feature-item">
                <div class="index-num">0{{ index + 1 }}</div>
                <div class="text-box">
                  <h4>{{ item.title }}</h4>
                  <p>{{ item.desc }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact-us" class="content-section text-center-wrapper" data-aos="fade-in">
        <h2 class="section-title">{{ $t('section.contact') }}</h2>
        <p class="text-muted mb-4">{{ $t('section.contact_sub') }}</p>
         <el-space :size="20">
          <el-button type="primary" size="large" @click="goToLogin" class="tech-btn-primary" round>{{ $t('footer.join') }}</el-button>
          <el-button size="large" round class="contact-btn" @click="goToContact">{{ $t('footer.contact') }}</el-button>
        </el-space>
      </section>

      <section id="how-to-join" style="height: 0; padding: 0; overflow:hidden;"></section>

    </div>
    <footer class="icp-footer">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备2026005929号</a>
    </footer>
  </div>
</template>

<script>
import { loadSlim } from "tsparticles-slim"
import axios from 'axios';
import {
  ArrowRight, DocumentCopy, DataAnalysis, Cpu, Connection, Setting, MagicStick, FirstAidKit
} from '@element-plus/icons-vue'
import VanillaTilt from 'vanilla-tilt';
// 【新增】引入 useI18n
import { useI18n } from 'vue-i18n'

export default {
  name: 'PublicHomeView',
  components: { ArrowRight, DocumentCopy, DataAnalysis, Cpu, Connection, Setting, MagicStick, FirstAidKit },

  // 【新增】setup 配置，用于获取 locale 变量
  setup() {
    const { locale } = useI18n()
    return { locale }
  },

  directives: {
    tilt: {
      mounted(el) {
        VanillaTilt.init(el, { max: 8, speed: 400, glare: true, "max-glare": 0.2, scale: 1.02 });
      },
      unmounted(el) {
        if (el.vanillaTilt) el.vanillaTilt.destroy();
      }
    }
  },
  data() {
    return {
      activeSection: 'about-us',
      partners: [],
      newsList: [],
      startAnimation: false,
      observer: null,
      policies: [],
      industryFields: [
        {name: '人工智能 (AI)', icon: 'Cpu', count: 0, code: 'ai'},
        {name: '新材料', icon: 'Connection', count: 0, code: 'material'},
        {name: '高端制造', icon: 'Setting', count: 0, code: 'manufacturing'},
        {name: '合成生物', icon: 'MagicStick', count: 0, code: 'bio'},
        {name: '生物医药', icon: 'FirstAidKit', count: 0, code: 'medical'},
      ],
      // 注意：serviceTargets 和 committees 移动到了 computed
      particlesOptions: {
        fpsLimit: 60,
        fullScreen: {enable: false},
        particles: {
          color: {value: "#ffffff"},
          links: {color: "#ffffff", distance: 150, enable: true, opacity: 0.2, width: 1},
          move: { enable: true, speed: 1 },
          number: {density: {enable: true, area: 800}, value: 60},
          opacity: {value: 0.5},
          shape: {type: "circle"},
          size: {value: {min: 1, max: 3}},
        },
        interactivity: {
          events: {onHover: {enable: true, mode: "grab"}, resize: true},
          modes: {grab: {distance: 140, links: {opacity: 1}}},
        },
        detectRetina: true,
      }
    };
  },
  computed: {
    // 【重要知识点】：为什么放到 computed 里？
    // 因为 data 里的数据在页面加载时就固定了，切换语言不会变。
    // computed 会监听 this.$t 的变化，语言一切换，这里的数据就会自动更新！
    serviceTargets() {
      return [
        { title: this.$t('about.gov'), desc: this.$t('about.gov_desc') },
        { title: this.$t('about.ent'), desc: this.$t('about.ent_desc') },
        { title: this.$t('about.park'), desc: this.$t('about.park_desc') },
        { title: this.$t('about.social'), desc: this.$t('about.social_desc') },
        { title: this.$t('about.uni'), desc: this.$t('about.uni_desc') }
      ];
    },
    // 下面这个暂时可以留在这里，虽然里面的文字是写死的中文，以后想翻译也可以照着 serviceTargets 做
    committees() {
      return [
        {title: '粤港澳大湾区人才中心', desc: '人才评价与发展服务', image: require('@/assets/images/gba-talent.jpg')},
        {title: '数字经济与产教融合', desc: '赋能产业数字化转型', image: require('@/assets/images/digital-economy.jpg')},
        {title: '创投工作委员会', desc: '投融资对接与孵化', image: require('@/assets/images/venture-capital.jpg')},
        {title: '成果转化工作委员会', desc: '科研成果落地与转化', image: require('@/assets/images/achievement.jpg')},
        {title: '法律工作委员会', desc: '提供法律咨询与合规指导', image: require('@/assets/images/legal.jpg')},
        {title: '科技教育工作委员会', desc: '推动科技与教育深度融合', image: require('@/assets/images/education.jpg')},
        {title: '财税合规工作委员会', desc: '企业财税规划与合规', image: require('@/assets/images/finance.jpg')},
        {title: '市场工作委员会', desc: '市场拓展与资源链接', image: require('@/assets/images/market.jpg')},
        {title: '公关传播工作委员会', desc: '品牌建设与媒体传播', image: require('@/assets/images/pr.jpg')}
      ]
    },
    researchGroups() {
      return this.chunkArray(this.partners.filter(p => p.category === 'RESEARCH'), 5);
    },
    universityGroups() {
      return this.chunkArray(this.partners.filter(p => p.category === 'UNIVERSITY'), 5);
    },
    enterpriseGroups() {
      return this.chunkArray(this.partners.filter(p => p.category === 'ENTERPRISE' || !p.category), 5);
    }
  },
  mounted() {
    this.fetchPartners();
    this.fetchNews();
    this.fetchPolicies();
    this.fetchReportStats();
  },
  methods: {
    // 【新增】切换语言逻辑
    toggleLang() {
      this.locale = this.locale === 'zh' ? 'en' : 'zh';
    },

    goToLogin() { this.$router.push('/login'); },
    goToNews() { this.$router.push('/news'); },
    goToNewsDetail(id) { this.$router.push(`/news/${id}`); },
    goToContact() { this.$router.push('/contact'); },
    openPolicyLink(url) {
      if (url) { window.open(url, '_blank'); } else { this.$message.warning("该政策暂无跳转链接"); }
    },
    goToReportList(field) {
      this.$router.push({ path: '/reports', query: { category: field.code } });
    },
    chunkArray(array, size) {
      const result = [];
      for (let i = 0; i < array.length; i += size) result.push(array.slice(i, i + size));
      return result;
    },
    async fetchPartners() {
      try {
        const response = await axios.get('http://127.0.0.1:8000/members/api/public/partners/');
        this.partners = response.data;
      } catch (error) { console.error('获取合作单位失败:', error); }
    },
    async fetchNews() {
      try {
        const response = await axios.get('http://127.0.0.1:8000/members/api/public/activities/');
        this.newsList = response.data.results;
      } catch (error) { console.error('获取新闻失败:', error); }
    },
    async particlesInit(engine) { await loadSlim(engine); },
    scrollTo(index) {
      const element = document.getElementById(index);
      if (element) {
        const offset = 100;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;
        window.scrollTo({top: offsetPosition, behavior: 'smooth'});
        this.activeSection = index;
      }
    },
    async fetchPolicies() {
      try {
        const res = await axios.get('http://127.0.0.1:8000/members/api/public/policies/');
        this.policies = res.data;
      } catch (e) { console.error(e); }
    },
    async fetchReportStats() {
      try {
        const res = await axios.get('http://127.0.0.1:8000/members/api/public/report-stats/');
        const stats = res.data;
        this.industryFields.forEach(field => {
          const target = stats.find(s => s.code === field.code);
          if (target) { field.count = target.count; }
        });
      } catch (e) { console.error(e); }
    }
  }
};
</script>

<style scoped>
/* 这里只展示你需要新增或修改的 CSS，原有的保持不变 */

/* === 导航栏布局修改 === */
.flex-container {
  display: flex;
  align-items: center;
  justify-content: space-between; /* 两端对齐：菜单在中，按钮在右 */
  position: relative;
  padding-right: 20px;
}

.centered-menu {
  flex-grow: 1;
  justify-content: center;
  border-bottom: none !important;
  background: transparent;
}

/* 语言切换按钮样式 */
.lang-switch-wrapper {
  flex-shrink: 0;
  margin-left: 20px;
}
.lang-btn {
  font-weight: 700;
  font-size: 16px;
  color: #409EFF;
  transition: all 0.3s;
}
.lang-btn:hover {
  transform: scale(1.1);
  color: #1e3c72;
}

/* === 其他原有 CSS === */
.public-home-container { background-color: #f5f7fa; overflow-x: hidden; }
.hero-banner { position: relative; width: 100%; height: 65vh; min-height: 500px; background: #0f172a url('~@/assets/banner.jpg') center/cover no-repeat; display: flex; align-items: center; justify-content: center; color: #fff; text-align: center; }
.particles-container, #tsparticles { position: absolute; inset: 0; z-index: 0; width: 100%; height: 100%; }
.hero-content-overlay { position: relative; z-index: 10; max-width: 1000px; padding: 20px; }
.hero-banner-title { font-size: 3.5rem; font-weight: 800; letter-spacing: 2px; margin-bottom: 20px; text-shadow: 0 4px 12px rgba(0,0,0,0.8); }
.hero-banner-description { font-size: 1.4rem; font-weight: 300; line-height: 1.8; opacity: 0.95; text-shadow: 0 2px 8px rgba(0,0,0,0.8); }
.highlight-text { border-bottom: 1px solid rgba(255,255,255,0.3); padding-bottom: 5px; display: inline-block; margin-top: 10px; }
.hero-tags { margin-top: 15px; font-size: 1rem; opacity: 0.8; letter-spacing: 1px; text-transform: uppercase; }
.tech-btn-primary { background-color: #409EFF; border-color: #409EFF; font-weight: 600; padding: 12px 30px; transition: transform 0.2s; }
.tech-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(64,158,255,0.4); }
.tech-btn-outline { background: transparent; color: #fff; border: 1px solid rgba(255,255,255,0.5); }
.tech-btn-outline:hover { background: rgba(255,255,255,0.1); border-color: #fff; color: #fff; }
.in-page-nav { background: #fff; box-shadow: 0 4px 20px rgba(0,0,0,0.05); z-index: 100; }
.nav-container { max-width: 1200px; margin: 0 auto; }
.main-content { max-width: 1280px; margin: 0 auto; padding: 0 20px; }
.content-section { padding: 80px 0; }
.bg-light { background-color: #fff; margin: 0 -2000px; padding: 80px 2000px; }
.section-header { text-align: center; margin-bottom: 50px; }
.section-title { font-size: 2.2rem; font-weight: 700; color: #1e293b; margin-bottom: 10px; text-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.section-subtitle { color: #409EFF; font-size: 0.8rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; }
.news-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; }
.news-wrapper { height: 100%; }
.news-card { background: #fff; border-radius: 12px; overflow: hidden; height: 100%; box-shadow: 0 4px 12px rgba(0,0,0,0.04); border: 1px solid #ebeef5; display: flex; flex-direction: column; cursor: pointer; transform-style: preserve-3d; }
.news-image-wrapper { height: 200px; position: relative; overflow: hidden; }
.news-image { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s; }
.news-card:hover .news-image { transform: scale(1.05); }
.news-content { padding: 25px; flex-grow: 1; display: flex; flex-direction: column; }
.news-title { font-size: 1.2rem; margin-bottom: 10px; color: #333; line-height: 1.4; font-weight: 600; }
.news-excerpt { font-size: 0.9rem; color: #666; flex-grow: 1; margin-bottom: 20px; }
.read-more-link { border-top: 1px solid #f0f0f0; padding-top: 15px; display: flex; justify-content: space-between; color: #409EFF; font-size: 0.9rem; align-items: center; }
.news-btn-wrapper { width: 100%; text-align: center; margin-top: 50px; display: block; clear: both; }
.view-more-btn { padding: 12px 40px; font-size: 1rem; font-weight: 500; letter-spacing: 1px; transition: all 0.3s; }
.view-more-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3); }
.bento-container { display: grid; gap: 20px; grid-template-columns: repeat(4, 1fr); grid-auto-rows: 220px; grid-template-areas: "a a b d" "c c b e" "f g h i"; }
.area-0 { grid-area: a; } .area-1 { grid-area: b; } .area-2 { grid-area: c; } .area-3 { grid-area: d; } .area-4 { grid-area: e; } .area-5 { grid-area: f; } .area-6 { grid-area: g; } .area-7 { grid-area: h; } .area-8 { grid-area: i; }
.bento-item-wrapper { width: 100%; height: 100%; }
.bento-card { position: relative; width: 100%; height: 100%; border-radius: 16px; overflow: hidden; background: #000; color: #fff; cursor: pointer; transform-style: preserve-3d; }
.card-bg-image { width: 100%; height: 100%; position: absolute; top:0; left:0; }
.card-bg-image img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s; }
.bento-card:hover .card-bg-image img { transform: scale(1.1); }
.card-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.2)); }
.card-inner { position: relative; z-index: 2; padding: 25px; height: 100%; display: flex; flex-direction: column; justify-content: flex-end; transform: translateZ(20px); }
.card-content h3 { font-size: 1.2rem; font-weight: 700; margin-bottom: 5px; text-shadow: 0 2px 5px rgba(0,0,0,0.7); }
.card-content p { font-size: 0.85rem; opacity: 0.9; margin-bottom: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-shadow: 0 1px 3px rgba(0,0,0,0.7); }
.highlight-tag { background: #409EFF; font-size: 12px; padding: 2px 8px; border-radius: 10px; margin-bottom: 10px; display: inline-block; }
.card-arrow { position: absolute; top: 20px; right: 20px; opacity: 0; transition: opacity 0.3s; background: rgba(255,255,255,0.2); width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.bento-card:hover .card-arrow { opacity: 1; }
.insights-container { display: flex; gap: 30px; flex-wrap: wrap; }
.sub-title { font-size: 1.2rem; font-weight: 700; color: #2c3e50; display: flex; align-items: center; gap: 8px; margin: 0; }
.policy-section { flex: 1; min-width: 300px; background: #fff; border-radius: 12px; padding: 25px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); border: 1px solid rgba(255,255,255,0.5); display: flex; flex-direction: column; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #f0f2f5; }
.policy-list { list-style: none; padding: 0; margin: 0; }
.policy-item { display: flex; align-items: center; padding: 12px 0; border-bottom: 1px dashed #eee; cursor: pointer; transition: all 0.2s; }
.policy-item:hover { background-color: #f9fafc; padding-left: 10px; border-radius: 4px; }
.policy-item:last-child { border-bottom: none; }
.policy-location { color: #409EFF; font-weight: 600; margin-right: 10px; min-width: 45px; }
.policy-title { flex-grow: 1; color: #606266; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-right: 10px; }
.policy-item:hover .policy-title { color: #409EFF; }
.policy-date { color: #909399; font-size: 0.85rem; min-width: 80px; text-align: right; }
.report-section { flex: 1.5; min-width: 300px; }
.fields-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-top: 20px; }
.field-card:first-child { grid-column: span 2; background: linear-gradient(135deg, #3a7bd5, #3a6073); }
.field-card:first-child .field-info h4, .field-card:first-child .field-info p, .field-card:first-child .field-icon { color: #fff !important; }
.field-card { background: #fff; border-radius: 12px; padding: 20px; display: flex; align-items: center; gap: 15px; cursor: pointer; transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); box-shadow: 0 4px 12px rgba(0,0,0,0.05); border: 1px solid transparent; position: relative; overflow: hidden; }
.field-card:hover { transform: translateY(-5px); box-shadow: 0 10px 25px rgba(64, 158, 255, 0.15); border-color: #a0cfff; }
.field-icon { width: 50px; height: 50px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; transition: all 0.3s; }
.field-color-0 { background: rgba(255, 255, 255, 0.2); color: #fff; }
.field-color-1 { background: #ecf5ff; color: #409EFF; }
.field-color-2 { background: #f0f9eb; color: #67C23A; }
.field-color-3 { background: #fdf6ec; color: #E6A23C; }
.field-color-4 { background: #fef0f0; color: #F56C6C; }
.field-info h4 { margin: 0 0 5px 0; font-size: 1.05rem; font-weight: 700; color: #2c3e50; }
.field-info p { margin: 0; font-size: 0.85rem; color: #909399; }
.field-arrow { margin-left: auto; color: #dcdfe6; transition: all 0.3s; }
.field-card:hover .field-arrow { color: #409EFF; transform: translateX(3px); }
.category-title { font-size: 1.2rem; border-left: 4px solid #409EFF; padding-left: 12px; margin-bottom: 25px; color: #333; font-weight: 600; }
.partner-row { display: flex; justify-content: space-around; padding: 10px 20px; }
.partner-item { display: flex; flex-direction: column; align-items: center; width: 18%; text-decoration: none; color: #666; transition: transform 0.3s; }
.partner-item:hover { transform: translateY(-5px); color: #409EFF; }
.partner-logo-wrapper { width: 100%; height: 100px; background: #fff; border: 1px solid #eee; border-radius: 12px; display: flex; align-items: center; justify-content: center; padding: 15px; margin-bottom: 10px; box-shadow: 0 4px 10px rgba(0,0,0,0.03); transition: all 0.3s; }
.partner-item:hover .partner-logo-wrapper { box-shadow: 0 8px 20px rgba(64,158,255,0.15); border-color: #dcdfe6; }
.partner-logo { max-width: 90%; max-height: 90%; object-fit: contain; }
.partner-name { margin-top: 10px; font-size: 0.9rem; color: #555; font-weight: 600; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; width: 100%; display: block; }
.partner-category-block { margin-bottom: 40px; }
.dark-card-wrapper { background: url('~@/assets/about-bg.png'); background-position: center bottom; background-size: cover; background-repeat: no-repeat; min-height: 600px; border-radius: 24px; overflow: hidden; position: relative; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4); margin-top: 40px; display: flex; flex-direction: column; justify-content: center; }
.about-container { display: flex; position: relative; z-index: 2; height: 100%; }
.about-left { flex: 0 0 40%; padding: 60px; background: rgba(23, 37, 84, 0.7); backdrop-filter: blur(4px); border-right: 1px solid rgba(255, 255, 255, 0.1); display: flex; flex-direction: column; justify-content: center; }
.section-header-left { font-size: 2.8rem; font-weight: 800; margin-bottom: 24px; color: #ffffff !important; text-shadow: 0 2px 4px rgba(0,0,0,0.6); letter-spacing: 2px; }
.dot { color: #409EFF; }
.about-lead { color: rgba(255, 255, 255, 0.9); font-size: 1.2rem; line-height: 1.8; margin-bottom: 40px; text-shadow: 0 2px 4px rgba(0,0,0,0.5); font-weight: 300; }
.decoration-line { width: 60px; height: 4px; background: #409EFF; border-radius: 2px; margin-bottom: 40px; box-shadow: 0 0 10px rgba(64, 158, 255, 0.5); }
.about-btn { width: fit-content; font-weight: 600; letter-spacing: 1px; }
.about-right { flex: 1; padding: 30px 40px; background: rgba(15, 23, 42, 0.6); display: flex; flex-direction: column; justify-content: center; }
.feature-item { display: flex; align-items: flex-start; padding: 12px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.1); transition: transform 0.3s ease; }
.feature-item:last-child { border-bottom: none; }
.feature-item:hover { transform: translateX(10px); }
.index-num { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 2rem; font-weight: 700; line-height: 1; margin-right: 20px; flex-shrink: 0; background: linear-gradient(180deg, #409EFF 0%, rgba(64, 158, 255, 0.4) 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; padding-top: 2px; }
.text-box h4 { color: #fff; font-size: 1.1rem; margin-top: 0; margin-bottom: 4px; font-weight: 600; text-shadow: 0 2px 4px rgba(0,0,0,0.5); }
.text-box p { color: rgba(255, 255, 255, 0.75); font-size: 0.9rem; line-height: 1.4; margin: 0; text-align: justify; text-shadow: 0 1px 2px rgba(0,0,0,0.5); }
.text-center-wrapper { text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.text-muted { color: #6c757d; }
 .icp-footer { padding: 16px 0 24px; text-align: center; color: #909399; font-size: 12px; }
 .icp-footer a { color: #909399; text-decoration: none; }
 .icp-footer a:hover { color: #409EFF; }
@media (max-width: 992px) {
  .hero-banner-title { font-size: 2.5rem; }
  .bento-container { grid-template-columns: 1fr; grid-auto-rows: 200px; grid-template-areas: none; }
  .bento-item-wrapper { grid-area: auto !important; }
  .about-container { flex-direction: column; }
  .about-left { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.05); padding: 50px 30px; }
  .about-right { padding: 40px 20px; }
  .insights-container { flex-direction: column; }
  .fields-grid { grid-template-columns: 1fr; }
  .field-card:first-child { grid-column: span 1; }
  .news-grid { grid-template-columns: 1fr; }
}
</style>
